import { useNavigate } from "react-router-dom";
import { useGeo } from "@/lib/geo";

/**
 * HomeSeoSections — the SEO content block that lives BELOW the game canvas
 * on the homepage. Everything here is fully rendered in the initial HTML
 * (no click-to-load) so Googlebot indexes it the same as above-the-fold
 * content. Users see it when they scroll past the game.
 *
 * India visitors get UPI/cash keyword copy; global visitors get skill/
 * leaderboard copy. Both variants ship identical structure (H1 hidden but
 * present for accessibility, H2 About, FAQ grid, Related Searches, Footer)
 * so no cloaking.
 */
export default function HomeSeoSections() {
  const navigate = useNavigate();
  const { isIndia } = useGeo();

  const faqItems = isIndia
    ? [
        { q: "How do I play the Chrome Dino Game online?", a: "Just start playing above — tap the screen or press Space to jump over cactus obstacles and flying dragons. Sign in with Google only when you want to save your best run to the leaderboard." },
        { q: "Is DareDino the same as the Google Chrome dinosaur game?", a: "Yes — DareDino is an enhanced online T-Rex Runner. Same one-button jump mechanic as the offline chrome dino game, plus dragon obstacles, live leaderboard, and daily UPI skill rewards." },
        { q: "Is the dino game free and unblocked?", a: "Absolutely. DareDino is a 100% free chrome dino game unblocked in the browser. No download, no install. Only the daily top 3 unlock UPI skill rewards." },
        { q: "How does the daily UPI skill reward work?", a: "Daily top 3 players on the leaderboard unlock the UPI collector inside the game. Rewards are paid daily and weekly based on skill ranking — 100% skill-based, no entry fee, no betting." },
        { q: "Does it work on mobile?", a: "Yes — DareDino is a fully responsive chrome dino game online. Tap the screen or swipe up to jump. Runs smoothly on phones and tablets." },
        { q: "How is DareDino different from the offline dinosaur game?", a: "DareDino keeps the pure T-Rex Runner feel (60fps canvas, delta-time loop) and adds dragon obstacles, resume-with-ad on death, live leaderboards, and daily UPI skill rewards." },
      ]
    : [
        { q: "How do I play the Chrome Dino Game online?", a: "Just start playing above — tap the screen or press Space to jump over cactus obstacles and flying dragons. Sign in with Google only when you want to save your best run to the global leaderboard." },
        { q: "Is DareDino the same as the Google Chrome dinosaur game?", a: "Yes — DareDino is an enhanced online T-Rex Runner. Same one-button jump mechanic as the offline chrome dino game, plus dragon obstacles, resume-with-ad on death, and a live global leaderboard." },
        { q: "Is the dino game free and unblocked?", a: "Absolutely. DareDino is a 100% free chrome dino game unblocked in any modern browser. No download, no install, no signup wall — sign in with Google only to save your best run." },
        { q: "How does the global leaderboard work?", a: "Your best run is submitted with a signed HMAC payload and cross-verified server-side (score/duration/obstacles ratios) to prevent tampering. Rankings refresh every 3 seconds — daily boards reset at 00:00 UTC, weekly boards reset every Monday 00:00 UTC." },
        { q: "Does it work on mobile?", a: "Yes — DareDino is a fully responsive chrome dino game online. Tap the screen or swipe up to jump. Runs smoothly on phones and tablets at 60fps." },
        { q: "How is DareDino different from the offline dinosaur game?", a: "DareDino keeps the pure T-Rex Runner feel (60fps canvas, delta-time loop) and adds dragon obstacles, resume-with-ad on death, and a live verified global skill leaderboard." },
      ];

  const relatedKeywords = isIndia
    ? [
        "chrome dino game", "chrome dinosaur game", "google dino game", "t-rex runner",
        "dino run", "dino game unblocked", "chrome dino unblocked", "google chrome dinosaur game",
        "no internet game", "offline dinosaur game", "dinosaur game online", "dino jumping game",
        "t rex chrome", "chrome offline game", "google dinosaur", "play dino game",
        "chrome dino run", "dino skill game",
      ]
    : [
        "chrome dino game", "chrome dinosaur game", "google dino game", "t-rex runner",
        "dino run", "dino game unblocked", "chrome dino unblocked", "google chrome dinosaur game",
        "no internet game", "offline dinosaur game", "dinosaur game online", "dino jumping game",
        "t rex chrome", "chrome offline game", "google dinosaur", "play dino game",
        "endless runner game", "skill leaderboard game",
      ];

  // Programmatic SEO keyword landing pages we cross-link into. Each is a
  // real route in App.js (KEYWORD_SLUGS) so Googlebot follows them.
  const relatedPages = [
    { slug: "chrome-dino", label: "Chrome Dino" },
    { slug: "dino-game", label: "Dino Game" },
    { slug: "t-rex-game", label: "T-Rex Game" },
    { slug: "unblocked", label: "Unblocked" },
    { slug: "google-dino", label: "Google Dino" },
    { slug: "dino-run", label: "Dino Run" },
  ];

  return (
    <section
      data-testid="home-seo-sections"
      className="relative z-10 border-t border-[#00F0FF]/20 bg-[#0B0C10]/40 px-4 sm:px-6 md:px-12 py-16 mt-8"
    >
      {/* SR-only H1 — main keyword lives at the top of the DOM for SEO but is
          not visually shown (game canvas is the visual hero above). */}
      <h1 className="sr-only" data-testid="home-h1">
        Chrome Dino Game Online — Play Google T-Rex Runner Free with {isIndia ? "Real UPI Cash Rewards" : "Live Global Leaderboard"}
      </h1>

      <div className="max-w-4xl mx-auto space-y-12">
        <div>
          <h2 className="font-heading font-black text-2xl md:text-3xl text-white mb-4" data-testid="home-h2-about">
            About the <span className="text-[#EAEB18]">Chrome Dino Game</span>
          </h2>
          <p className="text-sm md:text-base text-[#94A3B8] leading-relaxed">
            The <strong className="text-white">Chrome Dino Game</strong> — also
            known as the <strong className="text-white">T-Rex Runner</strong>, the{" "}
            <strong className="text-white">Google Dinosaur Game</strong>, or simply
            the <em>no-internet game</em> — is the iconic endless runner that
            appears in Google Chrome when your connection drops. DareDino is the
            enhanced online, skill-based version of that classic{" "}
            <strong className="text-white">chrome dinosaur game</strong> you
            already love —{" "}
            {isIndia
              ? "with a live daily leaderboard and UPI skill rewards for the top three players every single day."
              : "with 60fps canvas physics, dragon obstacles, verified anti-cheat, and a live global leaderboard that refreshes every 3 seconds."}
          </p>
        </div>

        <div>
          <h2 className="font-heading font-black text-xl md:text-2xl text-white mb-4">
            How to Play
          </h2>
          <ol className="grid sm:grid-cols-2 gap-3 text-sm text-[#94A3B8]">
            <li className="border border-[#00F0FF]/25 bg-[#050505] p-4">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] block mb-1">1</span>
              <strong className="text-white">Press Space</strong> on desktop or <strong className="text-white">tap the screen</strong> on mobile to jump.
            </li>
            <li className="border border-[#00F0FF]/25 bg-[#050505] p-4">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] block mb-1">2</span>
              Avoid <strong className="text-white">cactus</strong> and duck under flying <strong className="text-white">pterodactyl / dragon</strong> enemies.
            </li>
            <li className="border border-[#00F0FF]/25 bg-[#050505] p-4">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] block mb-1">3</span>
              Survive the <strong className="text-white">rain phase</strong> — visibility drops and obstacles speed up.
            </li>
            <li className="border border-[#00F0FF]/25 bg-[#050505] p-4">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] block mb-1">4</span>
              Sign in with Google to save your best run and climb the {isIndia ? "daily UPI leaderboard" : "global leaderboard"}.
            </li>
          </ol>
        </div>

        <div>
          <h2 className="font-heading font-black text-xl md:text-2xl text-white mb-4">
            Frequently Asked Questions
          </h2>
          <div className="grid md:grid-cols-2 gap-3">
            {faqItems.map((item) => (
              <div key={item.q} className="border border-[#00F0FF]/25 bg-[#050505] p-5">
                <h3 className="font-heading font-bold text-sm md:text-base text-[#00F0FF] mb-2">
                  {item.q}
                </h3>
                <p className="text-xs md:text-sm text-[#94A3B8] leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-heading font-black text-xl md:text-2xl text-white mb-3">
            Explore More Dino Game Pages
          </h2>
          <div className="flex flex-wrap gap-2" data-testid="related-pages">
            {relatedPages.map((p) => (
              <a
                key={p.slug}
                href={`/${p.slug}`}
                className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-[#00F0FF] hover:text-white border border-[#00F0FF]/40 hover:border-[#EAEB18] px-3 py-1.5 bg-[#0B0C10] transition-colors"
              >
                {p.label} →
              </a>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-heading font-black text-xl md:text-2xl text-white mb-3">
            Related Searches
          </h2>
          <div className="flex flex-wrap gap-2">
            {relatedKeywords.map((kw) => (
              <span
                key={kw}
                className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-[#94A3B8] border border-[#00F0FF]/20 px-3 py-1 bg-[#0B0C10]"
              >
                {kw}
              </span>
            ))}
          </div>
        </div>

        <footer className="pt-8 border-t border-[#00F0FF]/20 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#94A3B8]/60" data-testid="footer-copyright">
            © DAREDINO 2026 · Skill-based{isIndia ? " · Daily Rewards" : " · Global"}
          </span>
          <nav className="flex flex-wrap gap-4 text-[10px] uppercase tracking-[0.3em]" data-testid="footer-legal-links">
            <button onClick={() => navigate("/pricing")} className="text-[#00F0FF] hover:text-white" data-testid="footer-pricing-link">Pricing</button>
            <button onClick={() => navigate("/privacy-policy")} className="text-[#94A3B8] hover:text-white" data-testid="footer-privacy-link">Privacy Policy</button>
            <button onClick={() => navigate("/terms-of-service")} className="text-[#94A3B8] hover:text-white" data-testid="footer-terms-link">Terms of Service</button>
          </nav>
        </footer>
      </div>
    </section>
  );
}
